import {
  acceptReview,
  applyBody,
  applyMetadata,
  bodyText,
  formatKey,
  normalizeForSearch,
  parseKey,
  sameContent,
  spellTonic,
  type Issue,
  type Notation,
  type RevisionContent,
  type SongDocument,
} from '../../domain'
import type { EditorDraft } from '../../storage/db'

/**
 * El estado del editor de fichas y su paso a una revisión. El texto del cuerpo se edita como
 * ChordPro; el tono, la cejilla, el tempo y el compás tienen su formulario (ADR 0005).
 */
export interface FormState {
  title: string
  artist: string
  body: string
  shapeKey: string
  capo: number
  tempoBpm: number | null
  timeSignature: string
  notes: string
  /** Aceptar lo que venía por revisar. */
  reviewed: boolean
}

export function formFromDocument(document: SongDocument): FormState {
  const { content } = document.revision
  return {
    title: document.song.title,
    artist: document.song.artist,
    body: bodyText(content),
    shapeKey: content.shapeKey ?? '',
    capo: content.capo,
    tempoBpm: content.tempo.reference?.bpm ?? null,
    timeSignature: content.timeSignature ?? '',
    notes: content.notes ?? '',
    reviewed: false,
  }
}

export interface Built {
  content: RevisionContent
  issues: Issue[]
}

/** La ficha que saldría de guardar ahora: el texto aplicado a la revisión principal. */
export function buildContent(base: RevisionContent, form: FormState): Built {
  const applied = applyBody(base, form.body)
  let content = applyMetadata(applied.content, {
    shapeKey: form.shapeKey.trim() === '' ? null : form.shapeKey,
    capo: form.capo,
    tempoBpm: form.tempoBpm,
    timeSignature: form.timeSignature === '' ? null : form.timeSignature,
    notes: form.notes.trim() === '' ? null : form.notes,
  })
  if (form.reviewed) content = acceptReview(content)
  return { content, issues: applied.issues }
}

/** ¿Hay algo que guardar? Compara con lo guardado, no con el texto: el formato no cuenta. */
export function isDirty(document: SongDocument, form: FormState, built: Built): boolean {
  return (
    form.title.trim() !== document.song.title ||
    form.artist.trim() !== document.song.artist ||
    !sameContent(built.content, document.revision.content)
  )
}

export function toDraft(
  arrangementId: string,
  baseRevisionId: string,
  form: FormState,
  now: string,
): EditorDraft {
  const draft: EditorDraft = {
    arrangementId,
    baseRevisionId,
    title: form.title,
    artist: form.artist,
    body: form.body,
    shapeKey: form.shapeKey,
    capo: form.capo,
    tempoBpm: form.tempoBpm,
    timeSignature: form.timeSignature,
    notes: form.notes,
    savedAt: now,
  }
  if (form.reviewed) draft.reviewed = true
  return draft
}

export function fromDraft(draft: EditorDraft): FormState {
  return {
    title: draft.title,
    artist: draft.artist,
    body: draft.body,
    shapeKey: draft.shapeKey,
    capo: draft.capo,
    tempoBpm: draft.tempoBpm,
    timeSignature: draft.timeSignature,
    notes: draft.notes,
    reviewed: draft.reviewed ?? false,
  }
}

/** Las 24 tonalidades, con la armadura más corta y escritas en la notación de la ficha. */
export function keyOptions(notation: Notation, current: string): string[] {
  const options: string[] = []
  for (let pc = 0; pc < 12; pc += 1) {
    for (const minor of [false, true]) {
      options.push(formatKey({ tonic: spellTonic(pc, minor, true), minor }, notation))
    }
  }
  const unique = [...new Set(options)]
  return current && !unique.includes(current) ? [current, ...unique] : unique
}

export const TIME_SIGNATURES = ['2/4', '3/4', '4/4', '5/4', '6/8', '7/8', '9/8', '12/8', '2/2']

export function timeOptions(current: string): string[] {
  return current && !TIME_SIGNATURES.includes(current)
    ? [current, ...TIME_SIGNATURES]
    : TIME_SIGNATURES
}

/** Una tonalidad escrita por la ficha que el programa entiende. */
export function isKnownKey(key: string): boolean {
  return parseKey(key) !== null
}

/** Nombre de archivo sin tildes ni espacios: «Qué bien» → «que-bien». */
export function fileSlug(title: string): string {
  const slug = normalizeForSearch(title)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug === '' ? 'cancion' : slug
}
